import React from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import type { SvgIconComponent } from "@mui/icons-material";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import PublicIcon from "@mui/icons-material/Public";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { profile } from "../../../content";
import { NAV_DESTINATIONS } from "../../../constants/navigation";
import type { NavDestination } from "../../../constants/navigation";
import type { SocialLink } from "../../../types/content";

import "./footer.css";
import "./footer.scss";
import SourishMukherjeeBrandLogo from "../sourishMukherjeebrandLogo/SourishMukherjeeLogo";
import { isConfigured, resetConsent } from "../../../analytics/analytics";

/**
 * Icon per `profile.socialLinks` id. A link whose id is absent here still
 * renders, with the generic globe — content is never dropped for want of an
 * icon (Article II.4 is about not shipping empty sections, not about hiding
 * real data).
 */
const SOCIAL_ICONS: Record<string, SvgIconComponent> = {
	linkedin: LinkedInIcon,
	credly: WorkspacePremiumIcon
};

/**
 * Everything except `website` — that entry is this site, so linking it from
 * this site's own footer is a self-reference with nowhere to go.
 */
const FOOTER_SOCIALS: SocialLink[] = profile.socialLinks.filter((link: SocialLink) => link.id !== "website");

/**
 * THE FOOTER STAYS A FLAT LIST, AND THAT IS THE RULING (D-068).
 *
 * The navbar groups its ten destinations because it has to: ten flat labels do
 * not fit one row at any width, measured. THE FOOTER HAS NO SUCH PRESSURE — it
 * already wraps freely across the full page width, so grouping here would buy
 * nothing and cost the one thing a footer is for. A footer is a sitemap, not a
 * control: its job is to show every destination AT ONCE, and folding ten links
 * behind three disclosures would hide them from a reader scanning for the one
 * they want and put them behind an interaction a crawler has no reason to
 * perform.
 *
 * What it DOES adopt is the navbar's ORDER. Both navigations render the same
 * export, so the two agree on sequence — About, then Work, then Expertise,
 * then Background — and the reader meets one information architecture rather
 * than two. The order differs from the page's own section order, deliberately:
 * consistency between the two navigations is worth more than agreement with a
 * scroll position that only exists on one of five routes.
 *
 * Only mounted destinations appear, as before (Constitution II.4) — that rule
 * now lives with the list, in `constants/navigation.ts`, instead of being
 * restated in each of the three components that render it.
 *
 * Anchors are root-relative (`/#about`) so they work from every route — a bare
 * `#about` resolves against the current path and matches nothing on /stories.
 * A route renders as a react-router <Link> via the `as` prop rather than as an
 * anchor; react-router does not scroll to a hash on client-side navigation, so
 * the two kinds must stay distinct.
 */
const FOOTER_NAV: ReadonlyArray<NavDestination> = NAV_DESTINATIONS;

const FooterContainer = styled.div`
	width: 100%;
	padding: 2rem 0;
	display: flex;
	justify-content: center;
	//background: linear-gradient(100.26deg, rgba(0, 102, 255, 0.05) 42.33%, rgba(150, 0, 225, 0.05) 127.07%);
`;

const FooterWrapper = styled.footer`
	width: 100%;
	max-width: 1200px;
	display: flex;
	flex-direction: column;
	gap: 14px;
	align-items: center;
	padding: 1rem;
	color: var(--text-primary);
`;

const Logo = styled.h1`
	font-weight: 600;
	font-size: 20px;
	color: var(--accent-interactive);
`;

const Nav = styled.nav`
	width: 100%;
	/* 800px held six labels on one row; eight measure 833px at the gap below, so
	   the cap moves with the set rather than the set wrapping inside a cap chosen
	   for a shorter one. Still well inside FooterWrapper's 1200px. */
	max-width: 900px;
	margin-top: 0.5rem;
	display: flex;
	flex-direction: row;
	/* Wraps at EVERY width, not only below 768px. It used to wrap only in the
	   mobile query, which held while the set was six labels and broke the moment
	   it became eight: at 2rem gaps the row measured 845px inside this 800px box,
	   overflowed it, and was then clipped by the overflow-x: hidden on Body — so
	   at 820px "About" started at -37 and "Stories" ended past the viewport, both
	   half-cut, with no scrollbar to reveal them. The navbar never had this
	   problem because NavItems already wraps by design.
	   The gap comes down with it so eight labels still fit one row (621px of text
	   + 7 x 24px = 789px < 800): wrapping alone would have pushed one lone link
	   onto a second row, which is a larger visual change than closing the gaps.
	   Wrap is the safety net for the ninth label, not the layout for the eighth. */
	flex-wrap: wrap;
	gap: 1.5rem;
	justify-content: center;
	@media (max-width: 768px) {
		gap: 1rem;
		justify-content: center;
		text-align: center;
		font-size: 12px;
	}
`;

/**
 * The three `:hover` rules in this file moved from `--accent-interactive` to
 * `--accent-interactive-text` on the owner's ruling. They were the site's last
 * real AA failures, and they were invisible for a structural reason worth
 * recording: THE RENDERED-PAGE SWEEP DOES NOT EXERCISE `:hover` AT ALL, so the
 * whole state was unmeasured rather than merely unfixed (D-037 flagged this and
 * left it). Measured here by forcing the pseudo-class through CDP.
 *
 * #854ce6 is the FILL token — it carries white text at 5.03:1 and is 3.35:1
 * when it *is* text on the footer ground. #9a6bec is the token for the second
 * job. Dark moves deliberately, as a recorded decision, exactly as D-037's four
 * fixes did; light is one value for both roles (#6d28d9) and does not move.
 */
const NavLink = styled.a`
	color: var(--text-primary);
	text-decoration: none;
	font-size: 1.2rem;
	transition: color 0.2s ease-in-out;
	&:hover {
		color: var(--accent-interactive-text);
	}
	@media (max-width: 768px) {
		font-size: 1rem;
	}
`;

const SocialMediaIcons = styled.div`
	display: flex;
	margin-top: 1rem;
`;

const SocialMediaIcon = styled.a`
	display: inline-block;
	margin: 0 1rem;
	font-size: 1.5rem;
	color: var(--text-primary);
	transition: color 0.2s ease-in-out;
	/* See NavLink above for why this is the -text token. */
	&:hover {
		color: var(--accent-interactive-text);
	}
`;

const ContactRow = styled.div`
	margin-top: 1rem;
	display: flex;
	flex-direction: row;
	flex-wrap: wrap;
	gap: 0.5rem 2rem;
	justify-content: center;
	align-items: center;
	font-size: 0.95rem;
	/* This read \${theme.soft2} until D-035, and NO SUCH KEY EXISTS on either
	   theme object. styled-components emitted "color: undefined", the
	   declaration was dropped as invalid, and the row inherited FooterWrapper's
	   primary text colour — so the softer contact register the author intended
	   never rendered. D-035 pinned it to what it actually painted rather than
	   silently restyling dark under cover of a refactor; D-037 takes the owner
	   ruling and completes the repair, so the row finally renders as intended.
	   --text-muted is the site's established "supporting detail" step and clears
	   AA on the footer ground in both themes (dark 6.58:1, light 6.79:1).
	   Same for Copyright. */
	color: var(--text-muted);
	@media (max-width: 768px) {
		gap: 0.5rem 1rem;
		font-size: 0.85rem;
	}
`;

/**
 * `span` and `a` share this: the location is not a link, the email is, and
 * they must sit on the same baseline with the same icon spacing.
 */
const ContactItem = styled.span`
	display: inline-flex;
	align-items: center;
	/* 24px MINIMUM, AND IT APPLIES TO THE LINK (D-107). The email measured
	   280x20 at 320px and 310x23 at 820px — under WCAG 2.2 SC 2.5.8's 24x24 —
	   on EVERY route, because this is the footer. It is the site's only contact
	   control, so it is also the worst one to have be fiddly on a phone. The
	   rule sits on the shared element rather than on the anchor so the email and
	   the location beside it keep the same baseline; the span gaining a
	   min-height costs nothing, since it already sits in a flex row.
	   NO BACKTICKS IN THIS COMMENT: it lives inside a styled-components
	   template literal, and a backtick closes the literal it sits in. Caught by
	   tsc as "',' expected" on the line below, which names neither the cause
	   nor the file's real problem — the same trap D-088 and D-098 record. */
	min-height: 24px;
	gap: 0.4rem;
	color: inherit;
	text-decoration: none;
	svg {
		font-size: 1.1rem;
	}
	/* Only the email is a link, so only it has a hover state. See NavLink. */
	&[href]:hover {
		color: var(--accent-interactive-text);
	}
`;

const Copyright = styled.p`
	margin-top: 1.5rem;
	font-size: 0.9rem;
	/* See ContactRow — the same undefined \${theme.soft2}, repaired in D-037. */
	color: var(--text-muted);
	text-align: center;
`;

const Footer: React.FC = (): JSX.Element => {
	const handleScrollToTop = () => {
		window.scrollTo({
			top: 0,
			behavior: "smooth"
		});
	};

	return (
		<FooterContainer>
			<FooterWrapper>
				<div className="text-gradient-footer">{profile.name}</div>
				<Nav>
					{FOOTER_NAV.map((item: NavDestination) =>
						item.to ? (
							<NavLink key={item.label} as={Link} to={item.to}>
								{item.label}
							</NavLink>
						) : (
							<NavLink key={item.label} href={item.href}>
								{item.label}
							</NavLink>
						)
					)}
				</Nav>
				<SocialMediaIcons>
					{FOOTER_SOCIALS.map((link: SocialLink) => {
						const Icon = SOCIAL_ICONS[link.id] ?? PublicIcon;
						return (
							<SocialMediaIcon
								key={link.id}
								href={link.url}
								target="_blank"
								rel="noopener noreferrer"
								aria-label={link.label}
							>
								<Icon />
							</SocialMediaIcon>
						);
					})}
				</SocialMediaIcons>
				<ContactRow>
					<ContactItem as="a" href={`mailto:${profile.email}`}>
						<EmailIcon />
						{profile.email}
					</ContactItem>
					<ContactItem>
						<LocationOnIcon />
						{profile.location.display}
					</ContactItem>
				</ContactRow>
				<SourishMukherjeeBrandLogo />
				<Copyright>
					&copy; {new Date().getFullYear()} {profile.name}. All rights reserved.
				</Copyright>
				{/* THE WITHDRAWAL CONTROL (D-104). Consent has to be as easy to take
				    back as it was to give, under the DPDPA and the GDPR alike, and a
				    banner that appears once is not that. It renders only when the
				    build carries a measurement ID — offering to change a preference
				    that governs nothing would be chrome pretending to be a control.
				    `isConfigured()` is false for the whole life of an unconfigured
				    build, so this subtree never renders there — which is the build
				    every gate in this repository runs against. It is not removed
				    from the bundle, and `analytics.ts` records why that claim was
				    checked and withdrawn. */}
				{isConfigured() && (
					<button
						type="button"
						onClick={resetConsent}
						className="mt-3 rounded-[6px] text-xs font-medium underline underline-offset-4 opacity-80 transition-opacity hover:opacity-100 focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--ring-accent)]"
					>
						Analytics preferences
					</button>
				)}
			</FooterWrapper>
			{/* <div className="scroll scroll--active">
				<button onClick={handleScrollToTop} className="top-btn" type="button">
					<span className="sr-only">Scroll to Top</span>
				</button>
			</div> */}
		</FooterContainer>
	);
};

export default Footer;
